<script setup>
import { showModal, hideModal } from '@/Utils/modalBus'
import { computed, ref } from 'vue'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'
import { useNotification } from '@kyvg/vue3-notification'

import FormButton from '@/components/Atoms/FormButton.vue'
import IconBase from '@/components/icon/IconBase.vue'
import BackButton from '@/components/Atoms/BackButton.vue'
import IconBin from '@/components/icon/IconBin.vue'
import Tooltip from '@/components/Atoms/Tooltip.vue'
import Title from '@/components/ui/Title.vue'
import ToggleStreamModal from '@/components/modals/ToggleStreamModal.vue'
import ConfirmModal from '@/components/modals/ConfirmModal.vue'
import StreamService from '@/services/StreamService'

const props = defineProps({
	mediaPulse: {
		type: Object,
		default: null,
	},
})

const emit = defineEmits(['enable-stream'])

const store = useStore()
const router = useRouter()
const { notify } = useNotification()

const playout = computed(() => store.getters['Streams/tvPlayout'])
const playoutConfig = computed(() => store.getters['Playout/playoutConfig'])
const allInOneStreams = computed(() => store.getters['Streams/allInOneStreams'])

const fpsValue = computed(() =>
	playoutConfig.value?.settings?.fps || playout.value?.config?.fps || 25,
)
const resolutionValue = computed(() => {
	const raw = playoutConfig.value?.settings?.resolution || playout.value?.config?.resolution
	if (raw === 'fullhd') return '1920x1080'
	if (raw === 'hd' || !raw) return '1280x720'
	return raw
})
const resolutionLabel = computed(() => {
	const map = {
		'3840x2160': '4K',
		'2560x1440': '1440p',
		'1920x1080': '1080p',
		'1280x720': '720p',
		'854x480': '480p',
		'640x360': '360p',
	}
	return map[resolutionValue.value] || resolutionValue.value
})

const toggling = ref(false)

const toggleLabel = computed(() => {
	if (toggling.value) return 'Saving…'
	return playout.value?.enabled ? 'Disable' : 'Enable'
})

const toggleStatus = (ev) => {
	ev?.preventDefault?.()
	ev?.stopPropagation?.()
	if (toggling.value) return
	emit('enable-stream')
	showModal('modalToggleStream' + playout.value._id, '#btnShow')
}

const onToggleProcessing = (value) => {
	toggling.value = !!value
}

const hasDestination = computed(() => {
	if (playoutConfig.value?.destination) return true
	return !!playout.value?.destination
})
const deleteTooltip = computed(() => {
	const destinationId = playoutConfig.value?.destination
	const destinationStream = destinationId
		? allInOneStreams.value?.find((stream) => stream._id === destinationId)
		: null
	const linkedStreamName = destinationStream?.name || playout.value?.destination
	return `This TV Playout cannot be deleted because it is being used as a source in ${linkedStreamName} livestream. To delete this TV Playout, first remove it as a source from ${linkedStreamName} livestream.`
})

const deleteMessage = ref('Are you sure you want to delete your TV Playout?')

const showDeleteModal = () => {
	showModal('delete-playout-' + playout.value._id)
}

const onDeletePlayout = async () => {
	hideModal('delete-playout-' + playout.value._id)
	try {
		await StreamService.deleteStream(playout.value._id)
		await store.dispatch('Streams/getStreams', true)
		notify({ group: 'info', text: 'TV Playout deleted.' })
		router.push({ name: 'TVPlayoutDashboardV2' })
	} catch (err) {
		console.log(err)
	}
}

const saveNewName = async (newName) => {
	if (!playout.value?._id) return
	const trimmed = (newName || '').trim()
	if (!trimmed || trimmed === playout.value.name) return
	try {
		await StreamService.setStreamName(playout.value._id, trimmed)
		await store.dispatch('Streams/getStreams', true)
		notify({ group: 'success', text: 'TV Playout name updated' })
	} catch (err) {
		notify({ group: 'error', text: err?.message || 'Failed to rename TV Playout' })
	}
}
</script>

<template>
	<header>
		<div class="flex items-start gap-1 md:gap-3 flex-wrap w-full">
			<!-- Back + title stay on one line; long names truncate instead of wrapping under the button. -->
			<div class="flex items-center gap-1 md:gap-3 min-w-0 flex-1">
				<BackButton :to="{ name: 'TVPlayoutDashboardV2' }" class="!hidden md:!inline-flex" />
				<Title
					:editable="true"
					:title="playout?.name"
					titleClasses="text-xl font-medium m-0 rounded min-w-0"
					maxWidth="min-w-0 max-w-[600px]"
					@changed="saveNewName"
				/>
			</div>
			<div class="flex items-center gap-2 ml-auto shrink-0">
				<FormButton type="secondary" :is-loading="toggling" :disabled="toggling" @click="toggleStatus">
					{{ toggleLabel }}
				</FormButton>
				<Tooltip v-if="hasDestination" :content="deleteTooltip">
					<FormButton type="danger" isIcon :disabled="hasDestination">
						<icon-base fill="none">
							<icon-bin />
						</icon-base>
					</FormButton>
				</Tooltip>
				<FormButton v-else type="danger" isIcon :disabled="toggling" @click="showDeleteModal">
					<icon-base fill="none">
						<icon-bin />
					</icon-base>
				</FormButton>
			</div>
			<div class="flex items-center gap-2 basis-full md:basis-full">
				<span class="text-tiny text-surface-8">Resolution: {{ resolutionLabel }}</span>
				<span class="text-tiny text-surface-8">•</span>
				<span class="text-tiny text-surface-8">{{ fpsValue }} fps</span>
			</div>
		</div>
		<ToggleStreamModal :stream="playout" @modal-toggle="toggleStatus" @toggle-processing="onToggleProcessing" />
		<ConfirmModal :modalId="'delete-playout-' + playout?._id" :message="deleteMessage" modal-type="danger"
			@modal-confirm="onDeletePlayout" />
	</header>
</template>

<style scoped></style>
